import { Delete, ExpandMore } from '@mui/icons-material';
import { Accordion, AccordionDetails, AccordionSummary, IconButton, Typography } from '@mui/material';
import { useTranslation } from 'next-i18next';
import React, { useEffect } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { MdAdd, MdLiveTv, MdWarning } from 'react-icons/md';
import { LiveStream, Simulcast } from '../../../interfaces/interfaces';
import { useSelector } from '../../../reduxToolkit/typedRedux';
import { updateSimulcast } from '../../../services/services';
import VyButton from '../../common/VyButton';
import VyTextField from '../../common/VyTextField';
import VyTooltip from '../../common/VyTooltip';

export interface SimulcastComponentProps {
  onUpdate?: () => void;
  livestream?: LiveStream;
}

interface FormProps {
  simulcast: Simulcast[];
}

export const SimulcastComponent = (props: SimulcastComponentProps) => {
  const { onUpdate, livestream } = props;

  const { t } = useTranslation();
  const { register, handleSubmit, control, formState, reset } = useForm<FormProps>({});
  const { fields, append, remove } = useFieldArray({
    control,
    name: `simulcast`,
  });

  const eventChannel = useSelector(({ common }) => common.pusherChannels.eventChannel);

  function handleUpdate(props: FormProps) {
    updateSimulcast(livestream?._id!, props.simulcast).then(() => {
      onUpdate?.();
      reset({ simulcast: props.simulcast });
      toast.success(t('account:simulcast.update-success'));
    });
  }

  function getStatusIcon(status: string) {
    switch (status) {
      case 'errored':
        return <MdWarning />;
      case 'broadcasting':
        return <div className="w-5 h-5 bg-red-500 rounded-full" />;
      case 'idle':
      case 'starting':
        return <div className="w-5 h-5 rounded-full bg-slate-500" />;
      default:
        return null;
    }
  }

  useEffect(() => {
    if (livestream && livestream?.simulcast) {
      reset({ simulcast: livestream.simulcast });
    }
  }, [livestream]);

  useEffect(() => {
    if (eventChannel) {
      eventChannel.bind(
        'simulcasttarget-update',
        (data: { simulcastId: string; status: 'idle' | 'starting' | 'errored' | 'broadcasting'; livestreamId: string }) => {
          const allTargets = [...livestream?.simulcast!];
          const match = allTargets.find((i) => i.id === data.simulcastId);

          if (match) {
            const index = allTargets.indexOf(match);
            const clone = { ...match };
            clone.status = data.status;
            allTargets[index] = clone;
            reset({ simulcast: allTargets });
          }
        }
      );
    }

    return () => {
      eventChannel?.unbind('simulcasttarget-update');
    };
  }, [eventChannel]);

  return (
    <>
      <VyTooltip title={t('account:simulcast.explanation')}>
        <div className="flex items-center my-5 ">
          <MdLiveTv size="2vh" />
          <span className="ml-5">{t('account:simulcast.title')}</span>
        </div>
      </VyTooltip>
      <form onSubmit={handleSubmit(handleUpdate)}>
        {fields.map((field, index) => (
          <Accordion defaultExpanded={field['id'] === ''}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <div className="flex items-center">
                <VyTooltip title={t(`account:simulcast.status-${field['status']}`)}>
                  <div className="mr-3">{getStatusIcon(field['status'])}</div>
                </VyTooltip>
                <Typography>{field['passthrough']}</Typography>
              </div>
            </AccordionSummary>
            <AccordionDetails>
              <div key={field.id} className="flex flex-col w-full">
                {/* Convenient to have this in form to simplify the form's onSubmit */}
                {field['id'] && <input type="hidden" {...register(`simulcast.${index}.id`)} defaultValue={field['id']} />}
                <p className="mt-4">{t('account:simulcast.passthrough')}</p>
                <Controller
                  control={control}
                  name={`simulcast.${index}.passthrough`}
                  render={({ field }) => (
                    <VyTextField
                      {...field}
                      fullWidth
                      //@ts-ignore
                      defaultValue={field['passthrough']}
                    />
                  )}
                />
                <p className="mt-4">URL</p>
                <Controller
                  control={control}
                  name={`simulcast.${index}.url`}
                  render={({ field }) => (
                    <VyTextField
                      {...field}
                      fullWidth
                      //@ts-ignore
                      defaultValue={field['url']}
                    />
                  )}
                />
                <p className="mt-4">Key</p>
                <Controller
                  control={control}
                  name={`simulcast.${index}.stream_key`}
                  render={({ field }) => (
                    <VyTextField
                      {...field}
                      fullWidth
                      //@ts-ignore
                      defaultValue={field['stream_key']}
                    />
                  )}
                />

                <IconButton
                  onClick={() => {
                    remove(index);
                  }}
                  size="large">
                  <Delete />
                </IconButton>
              </div>
            </AccordionDetails>
          </Accordion>
        ))}
        <div className="flex justify-center w-full mt-2 flex-around">
          <VyTooltip title={t('account:simulcast.max-reached-tooltip')}>
            <IconButton
              disabled={fields.length >= 6}
              onClick={() => {
                append({ id: '', url: '', status: 'idle' });
              }}
              size="large">
              <MdAdd />
            </IconButton>
          </VyTooltip>
          {(formState.isDirty || (livestream?.simulcast && fields.length !== livestream?.simulcast?.length)) && (
            <VyButton type="submit">{t('account:simulcast.save-simulcasts')}</VyButton>
          )}
        </div>
      </form>
    </>
  );
};
